import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Refresh, Today } from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import notificationService from '../services/notificationService';
import siteService from '../services/siteService';
import MonitorPanel from '../components/monitor/MonitorPanel';
import { estimateLeakLitresPerHour } from '../utils/leakEstimate';

const toDateKey = (date) => format(date, 'yyyy-MM-dd');
const yesterdayKey = () => toDateKey(subDays(new Date(), 1));

const formatDateKey = (key) => {
  const m = String(key || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : key || '-';
};

const formatNumber = (value, digits = 1) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: digits }) : null;
};

const getReportDate = (row) => row?.data?.date || (row?.detectedAt ? toDateKey(new Date(row.detectedAt)) : null);
const getSiteNames = (row) => (row?.device?.sites || []).map((s) => s.description).filter(Boolean).join(', ');
const getDeviceName = (row) => [row?.device?.description, row?.device?.location].filter(Boolean).join(' — ');
const getLeakSize = (row) => {
  const stored = Number(row?.data?.estimatedLeakLitresPerHour);
  return Number.isFinite(stored) && stored > 0 ? stored : estimateLeakLitresPerHour(row);
};

const baseColumns = [
  {
    key: 'date',
    label: 'Date',
    getValue: getReportDate,
    render: (row) => formatDateKey(getReportDate(row)),
    sx: { whiteSpace: 'nowrap' }
  },
  { key: 'site', label: 'Site', getValue: getSiteNames, sx: { minWidth: 120 } },
  {
    key: 'serialNumber',
    label: 'Meter S/N',
    getValue: (row) => row?.device?.serialNumber || null,
    sx: { fontFamily: 'monospace', whiteSpace: 'nowrap' }
  },
  { key: 'device', label: 'Device / Position', getValue: (row) => getDeviceName(row) || null, sx: { minWidth: 140 } }
];

const leakColumns = [
  ...baseColumns,
  {
    key: 'leakSize',
    label: 'Possible Leak (L/h)',
    numeric: true,
    getValue: getLeakSize,
    render: (row) => {
      const v = formatNumber(getLeakSize(row), 2);
      return v == null ? '-' : <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>{v}</Typography>;
    }
  },
  {
    key: 'nightHours',
    label: 'Night Flow (h)',
    numeric: true,
    getValue: (row) => (Number(row?.data?.continuousNightHours) > 0 ? Number(row.data.continuousNightHours) : null)
  }
];

const thresholdColumns = [
  ...baseColumns,
  {
    key: 'usage',
    label: 'Usage',
    numeric: true,
    getValue: (row) => {
      const v = Number(row?.data?.usage ?? row?.data?.current);
      return Number.isFinite(v) ? v : null;
    },
    exportValue: (row) => row?.data?.usage ?? row?.data?.current ?? '',
    render: (row) => {
      const v = formatNumber(row?.data?.usage ?? row?.data?.current);
      return v == null ? '-' : <Typography variant="body2" sx={{ fontWeight: 600 }}>{`${v} ${row?.data?.unit || 'L'}`}</Typography>;
    }
  },
  {
    key: 'threshold',
    label: 'Threshold',
    numeric: true,
    getValue: (row) => {
      const v = Number(row?.data?.threshold);
      return Number.isFinite(v) ? v : null;
    },
    render: (row) => {
      const v = formatNumber(row?.data?.threshold);
      return v == null ? '-' : `${v} ${row?.data?.unit || 'L'}`;
    }
  }
];

const signalColumns = [
  ...baseColumns,
  {
    key: 'readings',
    label: 'Readings',
    numeric: true,
    getValue: (row) => {
      const v = Number(row?.data?.readingCount);
      return Number.isFinite(v) ? v : null;
    }
  },
  {
    key: 'expected',
    label: 'Expected / Day',
    numeric: true,
    getValue: (row) => {
      const v = Number(row?.data?.threshold);
      return Number.isFinite(v) ? v : null;
    }
  }
];

const Monitor = () => {
  const theme = useTheme();
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [startDate, setStartDate] = useState(yesterdayKey);
  const [endDate, setEndDate] = useState(yesterdayKey);
  const [status, setStatus] = useState('open');
  const [notifications, setNotifications] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    siteService.fetchSites()
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setSites([...arr].sort((a, b) => String(a.description || '').localeCompare(String(b.description || ''))));
      })
      .catch(() => setError('Failed to load sites.'));
  }, []);

  const siteIds = useMemo(() => selectedSites.map((s) => s.id), [selectedSites]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationService.getMonitorNotifications({ startDate, endDate, siteIds, status });
      setNotifications(Array.isArray(result?.notifications) ? result.notifications : []);
      setTruncated(Boolean(result?.truncated));
    } catch (err) {
      console.error('Error loading monitor notifications:', err);
      setError('Failed to load monitor data. Please try again.');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, siteIds, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdateStatus = async (id, nextStatus) => {
    try {
      await notificationService.updateNotificationStatus(id, nextStatus);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, status: nextStatus } : n)));
    } catch (err) {
      console.error('Error updating notification status:', err);
      setError('Failed to update status. Please try again.');
    }
  };

  const byType = useMemo(() => {
    const groups = { leak_detected: [], threshold_exceeded: [], signal_strength: [] };
    notifications.forEach((n) => {
      if (groups[n.type]) groups[n.type].push(n);
    });
    return groups;
  }, [notifications]);

  const rangeLabel = startDate === endDate ? startDate : `${startDate}_to_${endDate}`;
  const isPreviousDay = startDate === yesterdayKey() && endDate === startDate;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" component="h2">Monitor</Typography>
          <Typography variant="body2" color="text.secondary">
            Possible leaks, threshold exceedances and poor signal across your sites
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`${byType.leak_detected.length} leaks`} sx={{ backgroundColor: `${theme.palette.error.main}20`, color: theme.palette.error.main, fontWeight: 'bold' }} />
          <Chip label={`${byType.threshold_exceeded.length} thresholds`} sx={{ backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 'bold' }} />
          <Chip label={`${byType.signal_strength.length} poor signal`} sx={{ backgroundColor: '#9c27b020', color: '#9c27b0', fontWeight: 'bold' }} />
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={sites}
              value={selectedSites}
              onChange={(e, value) => setSelectedSites(value)}
              getOptionLabel={(option) => option.description || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} label="Sites" placeholder={selectedSites.length ? '' : 'All sites'} />
              )}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={startDate}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setStartDate(v);
                if (v > endDate) setEndDate(v);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={endDate}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setEndDate(v);
                if (v < startDate) setStartDate(v);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="monitor-status-label">Status</InputLabel>
              <Select labelId="monitor-status-label" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="viewed">Viewed</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2} sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant={isPreviousDay ? 'contained' : 'outlined'}
              startIcon={<Today />}
              onClick={() => {
                const key = yesterdayKey();
                setStartDate(key);
                setEndDate(key);
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Prev day
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} color="primary">
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Too many results to show them all. Narrow the date range or select specific sites.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} xl={6}>
          <MonitorPanel
            title="Possible Leaks"
            color={theme.palette.error.main}
            rows={byType.leak_detected}
            columns={leakColumns}
            defaultSort={{ key: 'leakSize', direction: 'desc' }}
            onUpdateStatus={handleUpdateStatus}
            exportFileName={`possible_leaks_${rangeLabel}.csv`}
            emptyText="No possible leaks for this period"
          />
        </Grid>
        <Grid item xs={12} xl={6}>
          <MonitorPanel
            title="Threshold Exceeded"
            color={theme.palette.primary.main}
            rows={byType.threshold_exceeded}
            columns={thresholdColumns}
            defaultSort={{ key: 'usage', direction: 'desc' }}
            onUpdateStatus={handleUpdateStatus}
            exportFileName={`threshold_exceeded_${rangeLabel}.csv`}
            emptyText="No thresholds exceeded for this period"
          />
        </Grid>
        <Grid item xs={12} xl={6}>
          <MonitorPanel
            title="Poor Signal"
            color="#9c27b0"
            rows={byType.signal_strength}
            columns={signalColumns}
            defaultSort={{ key: 'readings', direction: 'asc' }}
            onUpdateStatus={handleUpdateStatus}
            exportFileName={`poor_signal_${rangeLabel}.csv`}
            emptyText="No poor signal alerts for this period"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Monitor;
